import {PrismicNextImage} from '@prismicio/next'
import {useLinkResolver} from 'prismicio'
import {HeroV3SliceBrandFocus} from 'prismicio-types'
import {FC, useState} from 'react'
import {LogoBarV2} from 'slices/LogoBar/v2/LogoBarV2'
import {ButtonV2} from 'src/common/ButtonV2'
import {getButtonsData} from 'src/common/helpers'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {Icon} from 'src/common/Icon'
import {ModalVideoPlayer} from 'src/common/ModalVideoPlayer'
import {Context} from 'src/common/types'
import styles from './heroV3Styles.module.css'

type HeroV3BrandFocusProps = {
  slice: HeroV3SliceBrandFocus & {
    slice_type: string
    variation: string
  }
  componentIndex: number
  context: Context
}

export const HeroV3BrandFocus: FC<HeroV3BrandFocusProps> = ({slice, componentIndex, context}) => {
  const {animated_button} = slice.primary
  const {linkResolver} = useLinkResolver()
  const logos = slice.primary.logos.map((logo) => logo.logo)
  const sanitizedHeading = sanitizeHtml(slice.primary.heading!)
  const [isVideoOpen, setIsVideoOpen] = useState(false)

  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    primaryButton: slice.primary.primary_button,
    buttonsData: context.buttonsData,
    secondaryButton: slice.primary.secondary_button,
  })

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative isolate overflow-hidden bg-[#91958F] max-lg:bg-[linear-gradient(90deg,#939791_0.56%,#7F827D_100.98%)]"
      data-nav-dark="true"
    >
      <PrismicNextImage
        field={slice.primary.background_image.large}
        className="absolute inset-0 -z-10 h-full w-full object-cover max-[1670px]:hidden"
      />
      <PrismicNextImage
        field={slice.primary.background_image}
        className="absolute inset-0 -z-10 h-full w-full object-cover max-lg:hidden min-[1670px]:hidden"
      />
      <PrismicNextImage
        field={slice.primary.background_image.tablet}
        className="absolute bottom-0 left-0 -z-10 w-full max-md:hidden lg:hidden"
      />
      <PrismicNextImage
        field={slice.primary.background_image.mobile}
        className="absolute bottom-0 left-0 -z-10 w-full md:hidden"
      />
      <div className="absolute top-0 z-[-5] w-full bg-[linear-gradient(180deg,#7A7E77_11.17%,rgba(122,126,119,0)_90.79%)] backdrop-blur-[10px] max-md:hidden md:h-[450px] lg:hidden" />
      <div className="absolute bottom-0 z-[-5] h-[179px] w-full bg-[linear-gradient(0deg,#7A7E77_11.17%,rgba(122,126,119,0)_90.79%)] md:h-[260px]" />
      <div className="relative  mx-auto flex w-full max-w-[1200px] flex-col gap-10 px-5 pb-5 pt-[122px] md:px-10 md:pb-9 md:pt-[128px] lg:px-5 lg:pb-[50px] lg:pt-[170px] xl:px-0">
        {/* Content */}
        <div className="flex w-full flex-col gap-6 xl:gap-24">
          <div className="relative flex w-full flex-col gap-6 max-lg:items-center max-lg:self-center max-lg:text-center lg:max-w-[621px]">
            <button onClick={() => setIsVideoOpen(true)} className={`group/button w-fit ${styles.tagBox} h-[38px]`}>
              <div className="relative z-10 flex h-full w-full items-center justify-center gap-2 rounded-full bg-[#8D918B] pl-2 pr-3">
                <div className="absolute inset-0 h-full w-full rounded-full bg-moss-white/[10%] opacity-0 transition-opacity duration-300 ease-in-out group-hover/button:opacity-100" />
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-moss-white">
                  <Icon name="play" className="h-4 w-4 text-[#6E706C]" />
                </div>
                <span className="text-nowrap text-15 font-medium leading-5 text-moss-white">
                  {animated_button[0]?.label}
                </span>
              </div>
            </button>
            <h1
              className="md:text-tg-h1 text-[40px] font-normal leading-[42px] tracking-[-.4px] text-moss-white"
              dangerouslySetInnerHTML={{__html: sanitizedHeading}}
            />
            <p className="text-tg-body-m max-w-[432px] text-moss-white">{slice.primary.description}</p>
            <div className="flex flex-wrap gap-3 pt-2 max-md:justify-center md:gap-4">
              <ButtonV2
                className="uppercase max-md:w-full"
                variant="primary"
                size="medium"
                trackingData={{
                  location: `component-${componentIndex}-hero-v3-brand-focus`,
                  dataType: 'primary-button',
                  pageName: context.pageUid,
                  mktgId: primaryButtonData?.marketing_id || '',
                }}
                href={linkResolver(primaryButtonData?.link, 'primary-button-hero')}
              >
                {primaryButtonData?.label}
              </ButtonV2>
              {secondaryButtonData && (
                <ButtonV2
                  className="uppercase max-md:w-full"
                  variant="secondary"
                  size="medium"
                  trackingData={{
                    location: `component-${componentIndex}-hero-v3-brand-focus`,
                    dataType: 'secondary-button',
                    pageName: context.pageUid,
                    mktgId: secondaryButtonData?.marketing_id || '',
                  }}
                  href={linkResolver(secondaryButtonData?.link, 'secondary-button-hero')}
                >
                  {secondaryButtonData?.label}
                </ButtonV2>
              )}
            </div>
          </div>
          <div className="absolute rounded-[6px] bg-moss-white/[30%] backdrop-blur-[10px] max-lg:bottom-[179px] max-md:left-5 md:w-[258px] md:rounded-lg md:max-lg:left-10 lg:right-5 lg:top-[276px]">
            <div className="relative flex w-full flex-col gap-2.5 p-2 md:gap-3 md:rounded-lg md:p-2.5">
              <div className="absolute inset-0 rounded-[6px] border border-moss-white/[10%] md:rounded-lg" />

              {slice.primary.side_asset_features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2.5 rounded-[4px] bg-moss-white p-[7px] md:p-2.5">
                  <PrismicNextImage field={feature.icon} className="h-[26px] w-[26px] md:h-8 md:w-8" />
                  <span className="text-10 font-medium leading-normal text-moss-grey-500 md:text-[13px]">
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="w-1 max-md:h-[100vw] min-[500px]:h-[110vw] md:h-[68vw] lg:hidden"></div>
          <LogoBarV2 title={slice.primary.logobar_text} logos={logos} disableFade />
        </div>
      </div>
      {isVideoOpen && (
        <ModalVideoPlayer
          src={slice.primary.animated_button[0]?.video_source.embed_url || ''}
          onClose={() => setIsVideoOpen(false)}
          className="lg:w-[60vw]"
          startMuted={false}
        />
      )}
    </section>
  )
}
